import { useState } from "react";
import { ScrollText, Search, X } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/content/EmptyState";
import { useAdminAuditLogs, type AdminAuditFilters } from "@/hooks/useAdmin";
import { formatRelative } from "@/lib/format";

export function AdminAuditLogPage() {
  // Two optional, mutually-handled filters (the handler prefers actor over
  // target when both are set, so we keep them as one "field + value" pair).
  const [field, setField] = useState<"actor" | "target">("actor");
  const [raw, setRaw] = useState("");
  const [applied, setApplied] = useState<AdminAuditFilters>({});

  const { data, isLoading, isError } = useAdminAuditLogs(applied);

  const onApply = () => {
    const v = raw.trim();
    setApplied(v ? { [field]: v } : {});
  };

  const onClear = () => {
    setRaw("");
    setApplied({});
  };

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1 text-xs font-medium text-text-muted">
          Filter by
          <select
            value={field}
            onChange={(e) => setField(e.target.value as "actor" | "target")}
            className="h-10 rounded-lg border border-border bg-surface px-3 text-sm text-text dark:bg-surface-2 focus-visible:border-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
          >
            <option value="actor">Actor (user_id)</option>
            <option value="target">Target (resource_id)</option>
          </select>
        </label>
        <Input
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onApply();
          }}
          placeholder={field === "actor" ? "Paste an actor user_id" : "Paste a target resource_id"}
          className="w-full max-w-xs"
        />
        <Button variant="secondary" onClick={onApply}>
          <Search className="h-4 w-4" />
          Filter
        </Button>
        {(applied.actor || applied.target) && (
          <Button variant="ghost" onClick={onClear}>
            <X className="h-4 w-4" />
            Clear
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      ) : isError ? (
        <EmptyState
          icon={ScrollText}
          title="Couldn't load audit logs"
          body="The audit trail failed to load. Try again."
        />
      ) : !data || data.length === 0 ? (
        <EmptyState icon={ScrollText} title="No audit entries" body="Nothing matched this filter." />
      ) : (
        <Card className="overflow-hidden p-0">
          <ul className="divide-y divide-border">
            {data.map((row) => (
              <li key={row.log_id} className="px-4 py-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone="brand">{row.action}</Badge>
                  {row.target_type ? (
                    <span className="text-xs text-text-muted">
                      {row.target_type}: <span className="font-mono">{row.target_resource_id}</span>
                    </span>
                  ) : null}
                  <span className="ml-auto text-xs text-text-subtle">
                    {formatRelative(row.timestamp)}
                  </span>
                </div>
                <p className="mt-1 text-xs text-text-muted">
                  by{" "}
                  <span className="font-medium text-text">
                    {row.actor_email || row.actor_user_id}
                  </span>
                  {row.actor_role ? ` (${row.actor_role})` : ""}
                </p>
                {row.notes ? (
                  <p className="mt-0.5 text-xs text-text-muted">{row.notes}</p>
                ) : null}
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
